import { DECISIONS } from './decisions.js'

/**
 * Branch rules (decision #2). สนามเป้า (id 1) is head office — it runs no
 * clinic — but old records still carry it. So:
 *
 *   forms    never offer it                     → formBranchOptions()
 *   filters  never offer it; อารีย์ covers both   → filterBranchOptions() + branchFilterIds()
 *   display  a branch-1 row reads "อารีย์"         → branchName()
 *
 * The audit found branch 1 hidden on some pages, shown as "สนามเป้า" on others
 * and as "อารีย์ (เดิมสนามเป้า)" on others again — which put two "อารีย์" in one
 * list. Pages on the kit use these and nothing else.
 *
 * `branches` = the /api/branches list ({ id, name, … }); ids may be numbers or strings.
 */
const HQ = DECISIONS.hqBranchId
const MERGED = DECISIONS.hqMergedInto
const idOf = (b) => Number(b?.id ?? b?.branch_id)
const nameOf = (b) => b?.name || b?.branch_name || `สาขา #${idOf(b)}`

/** Every real clinic branch, id order, head office removed. */
export function clinicBranches(branches) {
  return (Array.isArray(branches) ? branches : [])
    .filter((b) => Number.isFinite(idOf(b)) && idOf(b) !== HQ)
    .sort((a, b) => idOf(a) - idOf(b))
}

/** Options for a FORM field (where a record is created / edited). */
export function formBranchOptions(branches) {
  return clinicBranches(branches).map((b) => ({ value: idOf(b), label: nameOf(b) }))
}

/** Options for a FILTER. First option = all branches (value ''). */
export function filterBranchOptions(branches, { allLabel = 'ทุกสาขา' } = {}) {
  return [{ value: '', label: allLabel }, ...formBranchOptions(branches)]
}

/**
 * Filter value → the branch ids it matches, or null for "all branches".
 *   ''  → null      8 → [8, 1]      3 → [3]
 * Send this to the API (or use matchesBranch on the client) — never the raw value.
 */
export function branchFilterIds(value) {
  if (value === '' || value == null || value === 0 || value === '0') return null
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return n === MERGED ? [MERGED, HQ] : [n]
}

/** Client-side check: does a row's branch id pass the filter value? */
export function matchesBranch(rowBranchId, filterValue) {
  const ids = branchFilterIds(filterValue)
  return !ids || ids.includes(Number(rowBranchId))
}

/**
 * For feeds that hand back a branch NAME, not an id (joined `branch.name`): the
 * head office's name reads as the branch it merged into. Anything else passes through.
 */
export function branchNameFromText(name) {
  const s = String(name || '').trim()
  if (!s) return '—'
  return /สนามเป้า/.test(s) ? 'อารีย์' : s
}

/** The name to SHOW for a branch id. Head office reads as the branch it merged into. */
export function branchName(id, branches) {
  const n = Number(id)
  if (!Number.isFinite(n) || n === 0) return '—'
  const want = n === HQ ? MERGED : n
  const b = (Array.isArray(branches) ? branches : []).find((x) => idOf(x) === want)
  return b ? nameOf(b) : `สาขา #${want}`
}
